/**
 * What a map's edits do — `docs/property-maps-v2.md` §5.3.
 *
 * **There is no Save button** (§12 rule 9), so every completed shape, move,
 * rename and delete goes straight to the server. That makes three things this
 * hook's job rather than the canvas's:
 *
 * **The screen moves first.** A shape appears the moment it is drawn and the
 * request follows; if the request fails the shape goes back where it was and
 * the status pill says so. Waiting for a round trip before drawing would make
 * the map feel like a form.
 *
 * **Undo is a compensating request**, not a local rewind (§5.3). Each change
 * pushes its own inverse — a delete for an insert, the old values for a
 * patch, a re-create for a delete — and the stack is 50 deep and dies with
 * the open map. A re-created shape gets a new id, which is honest: the row
 * that was deleted is still deleted (rule 2) and this is a new one.
 *
 * **A version conflict is a question, never a silent overwrite.** `409
 * version_conflict` sets the pill to *Updated somewhere else · Refresh*
 * (§5.7) and the local change is rolled back, because the alternative is one
 * person's drawing quietly replacing another's.
 */
import { useCallback, useRef, useState } from 'react'
import { ApiError } from '@rowted/shared'
import {
  addFeature, addLayer, deleteFeature, deleteLayer, patchFeature, patchLayer,
  type FeatureBody, type LayerInput, type MapFeature, type MapLayer, type OneMapPayload,
} from './api.js'
import type { SaveState } from './editor/status-pill.js'

/** §5.3: "the stack is per open map, 50 deep, and cleared when you leave". */
const UNDO_DEPTH = 50

type Undoable = () => Promise<void>

export interface MapEditing {
  layers: MapLayer[]
  features: MapFeature[]
  save: SaveState
  canUndo: boolean
  undo: () => void
  drawFeature: (layerId: string, body: FeatureBody) => Promise<MapFeature | null>
  changeFeature: (id: string, body: Partial<FeatureBody> & { layerId?: string }) => Promise<void>
  dropFeature: (id: string) => Promise<void>
  newLayer: (body: LayerInput) => Promise<string | null>
  changeLayer: (id: string, body: Partial<LayerInput> & {
    crewCanAdd?: boolean; hiddenOnTickets?: boolean; sortOrder?: number
  }) => Promise<void>
  dropLayer: (id: string, moveTo?: string | null) => Promise<void>
}

export function useMapEditing(
  payload: OneMapPayload, onReload: () => void,
): MapEditing {
  const [layers, setLayers] = useState<MapLayer[]>(payload.layers)
  const [features, setFeatures] = useState<MapFeature[]>(payload.features)
  const [save, setSave] = useState<SaveState>({ kind: 'idle' })
  const undos = useRef<Undoable[]>([])
  const [depth, setDepth] = useState(0)

  /*
   * A re-read replaces the rows; a *different map* also empties the stack.
   *
   * Two separate conditions, and running only the second was the bug: adding
   * a layer re-fetches the same map, the id was unchanged, and the new layer
   * never reached the state the panel draws from — so **New layer** appeared
   * to do nothing. The payload's identity is the signal, because the tab only
   * replaces it when it has actually asked the server again.
   */
  const seen = useRef(payload)
  if (seen.current !== payload) {
    const sameMap = seen.current.map.id === payload.map.id
    seen.current = payload
    setLayers(payload.layers)
    setFeatures(payload.features)
    if (!sameMap) {
      /* An undo that would patch a shape on a map nobody is looking at is an
         undo nobody can see happen. */
      undos.current = []
      setDepth(0)
    }
  }

  const remember = useCallback((inverse: Undoable) => {
    undos.current = [...undos.current.slice(-(UNDO_DEPTH - 1)), inverse]
    setDepth(undos.current.length)
  }, [])

  /**
   * Run a write, and put the screen back if it fails.
   *
   * One wrapper rather than a try/catch at each call site, because the three
   * things that happen around every write — the pill, the rollback and the
   * conflict — are the same three every time, and the one that gets forgotten
   * is the rollback.
   */
  const write = useCallback(async <T, >(
    run: () => Promise<T>, rollback: () => void,
  ): Promise<T | null> => {
    setSave({ kind: 'saving' })
    try {
      const answer = await run()
      setSave({ kind: 'saved', at: Date.now() })
      return answer
    } catch (cause: unknown) {
      rollback()
      if (cause instanceof ApiError && cause.code === 'version_conflict') {
        setSave({ kind: 'conflict', who: null, onRefresh: onReload })
      } else {
        setSave({
          kind: 'failed',
          message: cause instanceof Error ? cause.message : 'That change did not save.',
        })
      }
      return null
    }
  }, [onReload])

  const drawFeature = useCallback(async (layerId: string, body: FeatureBody) => {
    const made = await write(() => addFeature(layerId, body), () => { /* nothing drawn yet */ })
    if (!made) return null
    setFeatures((was) => [...was, made])
    remember(async () => {
      await deleteFeature(made.id)
      setFeatures((was) => was.filter((one) => one.id !== made.id))
    })
    return made
  }, [write, remember])

  const changeFeature = useCallback(async (
    id: string, body: Partial<FeatureBody> & { layerId?: string },
  ) => {
    const before = features.find((one) => one.id === id)
    if (!before) return
    /* Optimistic: the shape moves under the pointer, not after the round trip. */
    setFeatures((was) => was.map((one) => (one.id === id ? { ...one, ...body } as MapFeature : one)))

    const after = await write(
      () => patchFeature(id, { ...body, version: before.version }),
      () => setFeatures((was) => was.map((one) => (one.id === id ? before : one))))
    if (!after) return

    setFeatures((was) => was.map((one) => (one.id === id ? after : one)))
    remember(async () => {
      const back = await patchFeature(id, {
        geometry: before.geometry,
        shape: before.shape,
        name: before.name ?? '',
        note: before.note,
        color: before.color,
        glyph: before.glyph,
        layerId: before.layerId,
        version: after.version,
      })
      setFeatures((was) => was.map((one) => (one.id === id ? back : one)))
    })
  }, [features, write, remember])

  const dropFeature = useCallback(async (id: string) => {
    const before = features.find((one) => one.id === id)
    if (!before) return
    setFeatures((was) => was.filter((one) => one.id !== id))

    const gone = await write(
      async () => { await deleteFeature(id); return true },
      () => setFeatures((was) => [...was, before]))
    if (!gone) return

    /*
     * A new row, with a new id. Rule 2: the deleted one keeps its
     * `deleted_at` and its audit trail, and pretending otherwise would be a
     * resurrection the log could not explain.
     */
    remember(async () => {
      const again = await addFeature(before.layerId, {
        kind: before.kind,
        geometry: before.geometry,
        shape: before.shape,
        name: before.name ?? '',
        note: before.note,
        color: before.color,
        glyph: before.glyph,
        photoFileId: before.photoFileId,
      })
      setFeatures((was) => [...was, again])
    })
  }, [features, write, remember])

  const newLayer = useCallback(async (body: LayerInput) => {
    const made = await write(() => addLayer(payload.map.id, body), () => { /* nothing added */ })
    if (!made) return null
    onReload()
    return made.id
  }, [payload.map.id, write, onReload])

  const changeLayer = useCallback(async (
    id: string,
    body: Partial<LayerInput> & {
      crewCanAdd?: boolean; hiddenOnTickets?: boolean; sortOrder?: number
    },
  ) => {
    const before = layers.find((one) => one.id === id)
    if (!before) return
    setLayers((was) => was.map((one) => (one.id === id ? { ...one, ...body } : one)))

    const after = await write(
      () => patchLayer(id, { ...body, version: before.version }),
      () => setLayers((was) => was.map((one) => (one.id === id ? before : one))))
    if (!after) return

    setLayers((was) => was.map((one) => (one.id === id ? after : one)))
    remember(async () => {
      const back = await patchLayer(id, {
        name: before.name,
        color: before.color,
        audience: before.audience,
        divisionIds: before.divisionIds,
        pinnedToTickets: before.pinnedToTickets,
        crewCanAdd: before.crewCanAdd,
        hiddenOnTickets: before.hiddenOnTickets,
        version: after.version,
      })
      setLayers((was) => was.map((one) => (one.id === id ? back : one)))
    })
  }, [layers, write, remember])

  /**
   * Deleting a layer is **not** undoable, and the menu confirms instead.
   *
   * Undoing it would mean re-creating the layer and every shape on it as new
   * rows, and the measurement parts that pointed at those shapes would still
   * be pointing at nothing (§5.7's delete rule says so in words). A confirm
   * that names the shape count and offers to move them is the honest guard.
   */
  const dropLayer = useCallback(async (id: string, moveTo?: string | null) => {
    await write(async () => { await deleteLayer(id, moveTo); return true }, () => { /* nothing */ })
    undos.current = []
    setDepth(0)
    onReload()
  }, [write, onReload])

  const undo = useCallback(() => {
    const last = undos.current.pop()
    setDepth(undos.current.length)
    if (!last) return
    setSave({ kind: 'saving' })
    last()
      .then(() => setSave({ kind: 'saved', at: Date.now() }))
      .catch(() => {
        setSave({ kind: 'failed', message: 'That could not be undone.' })
        onReload()
      })
  }, [onReload])

  return {
    layers,
    features,
    save,
    canUndo: depth > 0,
    undo,
    drawFeature,
    changeFeature,
    dropFeature,
    newLayer,
    changeLayer,
    dropLayer,
  }
}
